import React, { useState, useEffect } from 'react';
import { updateBooking, getEquipmentBlockedDates } from '../../services/supabase';
import { supabaseServiceRole } from '../../services/supabase';
import { Equipment } from '../../types';
import EquipmentSelector from './EquipmentSelector';
import DateRangeSelector from './DateRangeSelector';
import { differenceInDays } from 'date-fns';
import { toast } from 'react-hot-toast';
import { FiMinus, FiPlus } from 'react-icons/fi';

// Интерфейс для элемента выбранного оборудования
interface SelectedEquipmentItem {
    id: string;
    name: string;
    quantity: number;
    dailyRate: number;
    image_url?: string;
    maxQuantity?: number;
}

// Интерфейс для пропсов компонента редактирования бронирования
export interface EditBookingFormProps {
    bookingId: string;
    startDate: string;
    endDate: string;
    projectName?: string | null;
    equipment: SelectedEquipmentItem[];
    onClose: () => void;
    onSuccess: () => void;
}

const EditBookingForm: React.FC<EditBookingFormProps> = ({
    bookingId,
    startDate,
    endDate,
    projectName = '',
    equipment,
    onClose,
    onSuccess
}) => {
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        new Date(startDate),
        new Date(endDate)
    ]);
    // Отдельное состояние для передачи в DateRangeSelector для корректного отображения
    const [calendarDates, setCalendarDates] = useState<{
        initialStartDate: Date;
        initialEndDate: Date;
    }>({
        initialStartDate: new Date(startDate),
        initialEndDate: new Date(endDate)
    });
    const [selectedEquipment, setSelectedEquipment] = useState<SelectedEquipmentItem[]>(equipment || []);
    const [newProjectName, setNewProjectName] = useState(projectName || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [disabledDates, setDisabledDates] = useState<Date[]>([]);
    const [forceOpenCalendar, setForceOpenCalendar] = useState(false);
    const [key, setKey] = useState(Date.now()); // Ключ для принудительного обновления компонента

    // Функция для получения недоступных дат для всех элементов оборудования
    const fetchDisabledDatesForEquipment = async () => {
        if (!selectedEquipment || selectedEquipment.length === 0) return;

        try {
            console.log('Получаем недоступные даты для оборудования');

            // Собираем все ID оборудования
            const equipmentIds = selectedEquipment.map(item => item.id);

            // Создаем массив для хранения всех недоступных дат
            let allDisabledDates: Date[] = [];

            // Загружаем недоступные даты для каждого элемента оборудования
            for (const equipmentId of equipmentIds) {
                const { data: blockedDates, error } = await getEquipmentBlockedDates(equipmentId);

                if (error) {
                    console.error(`Ошибка при получении недоступных дат для ${equipmentId}:`, error);
                    continue;
                }

                // Добавляем недоступные даты в общий массив
                if (blockedDates && blockedDates.length > 0) {
                    allDisabledDates = [...allDisabledDates, ...blockedDates];
                }
            }

            // Удаляем дубликаты дат
            const uniqueDates = allDisabledDates.filter((date, index, self) =>
                self.findIndex(d => d.getTime() === date.getTime()) === index
            );

            console.log(`Получено ${uniqueDates.length} уникальных недоступных дат`);
            setDisabledDates(uniqueDates);
        } catch (error) {
            console.error('Ошибка при получении недоступных дат:', error);
            setDisabledDates([]);
        }
    };

    // Загрузка доступного оборудования и недоступных дат при инициализации
    useEffect(() => {
        // Загружаем информацию о максимальном доступном количестве для каждого элемента оборудования
        const loadEquipmentData = async () => {
            try {
                // Получаем ID всего оборудования
                const equipmentIds = equipment.map(item => item.id);
                
                console.log('Запрашиваем количество для оборудования:', equipmentIds);
                
                // Запрашиваем данные об оборудовании для получения максимального количества
                const { data: equipmentData, error } = await supabaseServiceRole
                    .from('equipment')
                    .select('id, quantity, name, daily_rate, image_url')
                    .in('id', equipmentIds);
                
                if (error) {
                    console.error('Ошибка при получении данных об оборудовании:', error);
                    return equipment;
                }
                
                // Логируем точные данные, полученные из базы
                console.log('Полученные данные об оборудовании из базы данных:', JSON.stringify(equipmentData));
                
                // Проверим структуру данных первого элемента для отладки
                if (equipmentData && equipmentData.length > 0) {
                    const firstItem = equipmentData[0];
                    console.log('Структура первого элемента:', {
                        id: firstItem.id,
                        name: firstItem.name,
                        quantity: firstItem.quantity,
                        typeOfQuantity: typeof firstItem.quantity,
                        isInteger: Number.isInteger(firstItem.quantity)
                    });
                }
                
                // Если данных нет, перезапрашиваем еще раз все доступное оборудование
                if (!equipmentData || equipmentData.length === 0) {
                    const { data: allEquipment, error: allError } = await supabaseServiceRole
                        .from('equipment')
                        .select('*');
                        
                    if (!allError && allEquipment) {
                        console.log('Загружено всё оборудование как запасной вариант:', allEquipment);
                        
                        // Создаем карту из всего оборудования
                        const allEquipMap: Record<string, any> = {};
                        allEquipment.forEach(item => {
                            if (item.id) {
                                allEquipMap[item.id] = item;
                                // Логируем данные оборудования для отладки
                                console.log(`Оборудование из запасного варианта: ${item.name}, quantity=${item.quantity}, тип: ${typeof item.quantity}`);
                            }
                        });
                        
                        // Используем данные из всего оборудования
                        const updatedEquipment = equipment.map(item => {
                            const equipDetails = allEquipMap[item.id];
                            // Определяем максимальное количество
                            let maxQuantity = 1; // значение по умолчанию
                            
                            if (equipDetails && typeof equipDetails.quantity === 'number') {
                                maxQuantity = equipDetails.quantity;
                                console.log(`Установлено maxQuantity=${maxQuantity} для ${item.name} из запасного варианта`);
                            }
                            
                            return {
                                ...item,
                                maxQuantity
                            };
                        });
                        
                        console.log('Обновленное оборудование с данными из запасного варианта:', updatedEquipment);
                        setSelectedEquipment(updatedEquipment as SelectedEquipmentItem[]);
                        return;
                    }
                }
                
                // Создаем карту id -> quantity
                const quantityMap: Record<string, number> = {};
                
                if (equipmentData && equipmentData.length > 0) {
                    console.log('Обработка данных оборудования:');
                    
                    equipmentData.forEach(item => {
                        if (item.id) {
                            // Устанавливаем quantity, проверяя что оно является числом
                            quantityMap[item.id] = typeof item.quantity === 'number' ? item.quantity : 0;
                            
                            // Подробное логирование для отладки
                            console.log(`ID: ${item.id}, Название: ${item.name}, Количество в базе: ${item.quantity}, Итоговое значение: ${quantityMap[item.id]}`);
                        }
                    });
                } else {
                    console.warn('Не получено данных об оборудовании из базы данных!');
                }
                
                // Для каждого оборудования устанавливаем maxQuantity
                const updatedEquipment = equipment.map(item => {
                    // Проверяем точное наличие ID в карте
                    let maxQuantity = 1; // значение по умолчанию
                    
                    if (quantityMap.hasOwnProperty(item.id)) {
                        maxQuantity = quantityMap[item.id];
                        console.log(`Найдено количество для ${item.name} (ID: ${item.id}): ${maxQuantity}`);
                    } else {
                        console.warn(`Не найдено количество для ${item.name} (ID: ${item.id}), используем значение по умолчанию: ${maxQuantity}`);
                    }
                    
                    return {
                        ...item,
                        maxQuantity
                    };
                });
                
                console.log('Обновленное оборудование с maxQuantity:', updatedEquipment);
                setSelectedEquipment(updatedEquipment as SelectedEquipmentItem[]);
            } catch (err) {
                console.error('Ошибка при загрузке данных об оборудовании:', err);
                
                // В случае ошибки используем данные из пропсов
                const formattedEquipment = equipment.map(item => ({
                    ...item,
                    category_id: '',
                    description: '',
                    available: true,
                    daily_rate: item.dailyRate,
                    quantity: item.quantity,
                    image_url: item.image_url || '',
                    year: null,
                    manufacturer: null,
                    model: null,
                    status: 'available',
                    created_at: null,
                    updated_at: null,
                    sort_order: null,
                    maxQuantity: item.maxQuantity || 1
                }));
                
                setSelectedEquipment(formattedEquipment as SelectedEquipmentItem[]);
            }
        };
        
        // Запускаем загрузку данных об оборудовании
        loadEquipmentData();

        // Загружаем недоступные даты для оборудования
        fetchDisabledDatesForEquipment();
    }, [equipment]);

    // Расчет общей стоимости бронирования
    const calculateTotalPrice = () => {
        if (!dateRange[0] || !dateRange[1] || selectedEquipment.length === 0) return 0;

        const days = Math.max(1, differenceInDays(dateRange[1], dateRange[0]) + 1);

        return selectedEquipment.reduce((total, item) => {
            // Проверяем наличие dailyRate и устанавливаем значение по умолчанию 0, если оно отсутствует
            // Учитываем только положительные значения dailyRate
            const rate = (item.dailyRate !== undefined && item.dailyRate !== null && item.dailyRate > 0) 
                ? item.dailyRate 
                : 0;
            return total + (rate * item.quantity * days);
        }, 0);
    };

    // Общая стоимость бронирования
    const totalPrice = calculateTotalPrice();

    // Обработчик изменения дат
    const handleDatesChange = (startDate: Date | null, endDate: Date | null) => {
        console.log('EditBookingForm: Даты изменены:', { startDate, endDate });

        // Обновляем dateRange для вычислений
        setDateRange([startDate, endDate]);

        // Всегда обновляем calendarDates для корректного отображения
        if (startDate) {
            setCalendarDates(prev => ({
                ...prev,
                initialStartDate: startDate
            }));
        }

        if (endDate) {
            setCalendarDates(prev => ({
                ...prev,
                initialEndDate: endDate
            }));
        }

        // Обновляем ключ для принудительной перерисовки календаря
        setKey(Date.now());

        // Если обе даты выбраны, закрываем календарь
        if (startDate && endDate) {
            setForceOpenCalendar(false);
        }
    };

    // Добавление/обновление оборудования в выбранном списке
    const handleEquipmentSelect = (equipment: Equipment, quantity: number) => {
        const existingIndex = selectedEquipment.findIndex(item => item.id === equipment.id);

        // Установим безопасное значение для daily_rate
        // Если цена не указана или равна 0, показываем предупреждение пользователю
        if (equipment.daily_rate === undefined || equipment.daily_rate === null || equipment.daily_rate === 0) {
            toast.error(`У оборудования "${equipment.name}" не указана цена. Пожалуйста, обратитесь к администратору.`);
        }
        
        const safeDaily_rate = equipment.daily_rate !== undefined && equipment.daily_rate !== null 
            ? equipment.daily_rate 
            : 0;
            
        // Получаем точное значение quantity из базы данных
        console.log(`Выбрано оборудование: ${equipment.name}, ID: ${equipment.id}`);
        console.log(`Данные оборудования:`, JSON.stringify(equipment));
        
        // Получаем актуальное максимальное количество из базы данных
        let fetchQuantity = async () => {
            try {
                const { data, error } = await supabaseServiceRole
                    .from('equipment')
                    .select('quantity')
                    .eq('id', equipment.id)
                    .single();
                
                if (error) {
                    console.error(`Ошибка при получении количества для ${equipment.name}:`, error);
                    return null;
                }
                
                console.log(`Количество из базы данных для ${equipment.name}:`, data?.quantity);
                return data?.quantity;
            } catch (err) {
                console.error(`Ошибка запроса количества для ${equipment.name}:`, err);
                return null;
            }
        }
        
        // Выполняем запрос и обрабатываем результат
        fetchQuantity().then(dbQuantity => {
            // Определяем максимальное количество на основе доступного оборудования
            let maxQuantity = 1; // Значение по умолчанию
            
            // Если получили значение из базы, используем его
            if (dbQuantity !== null && typeof dbQuantity === 'number') {
                maxQuantity = dbQuantity;
                console.log(`Установлено maxQuantity=${maxQuantity} из базы данных для ${equipment.name}`);
            } 
            // Иначе пробуем использовать значение из переданного объекта equipment
            else if (typeof equipment.quantity === 'number') {
                maxQuantity = equipment.quantity;
                console.log(`Установлено maxQuantity=${maxQuantity} из объекта equipment для ${equipment.name}`);
            }
            
            // Добавляем оборудование с полученным количеством
            if (existingIndex >= 0) {
                // Обновляем существующее оборудование
                const updatedEquipment = [...selectedEquipment];
                updatedEquipment[existingIndex] = {
                    ...updatedEquipment[existingIndex],
                    quantity: Math.min(quantity, maxQuantity),
                    maxQuantity
                };
                setSelectedEquipment(updatedEquipment);
                console.log(`Обновлено оборудование: ${equipment.name}, новое количество: ${Math.min(quantity, maxQuantity)}`);
            } else {
                // Добавляем новое оборудование
                setSelectedEquipment([
                    ...selectedEquipment,
                    {
                        id: equipment.id,
                        name: equipment.name,
                        quantity: Math.min(quantity, maxQuantity),
                        dailyRate: safeDaily_rate,
                        image_url: equipment.image_url || undefined,
                        maxQuantity
                    }
                ]);
                console.log(`Добавлено новое оборудование: ${equipment.name}, количество: ${Math.min(quantity, maxQuantity)}`);
            }
            
            // После изменения оборудования, обновляем недоступные даты
            setTimeout(fetchDisabledDatesForEquipment, 100);
        });
    };

    // Удаление оборудования из выбранного списка
    const handleRemoveEquipment = (equipmentId: string) => {
        setSelectedEquipment(selectedEquipment.filter(item => item.id !== equipmentId));

        // После изменения оборудования, обновляем недоступные даты
        setTimeout(fetchDisabledDatesForEquipment, 100);
    };

    // Обновляет количество выбранного оборудования
    const updateEquipmentQuantity = (index: number, newQuantity: number) => {
        // Проверяем, что новое количество не меньше 1
        if (newQuantity < 1) return;

        // Получаем текущий элемент оборудования по индексу
        const item = selectedEquipment[index];
        
        // Проверяем, что новое количество не превышает максимальное (если оно определено и не равно 0)
        // Если maxQuantity равно 0, позволяем установить хотя бы 1
        if (item.maxQuantity !== undefined && item.maxQuantity !== 0 && newQuantity > item.maxQuantity) {
            return;
        }

        // Обновляем массив выбранного оборудования
        const updatedEquipment = [...selectedEquipment];
        updatedEquipment[index] = {
            ...item,
            quantity: newQuantity
        };
        
        setSelectedEquipment(updatedEquipment);
        
        // Если maxQuantity равно 0, но пользователь увеличивает количество, показываем предупреждение
        if (item.maxQuantity === 0 && newQuantity > 1) {
            toast(`Внимание: увеличено количество для "${item.name}", хотя в наличии 0 шт.`, {
                icon: '⚠️',
                style: {
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    borderColor: '#ffeeba'
                }
            });
        }
        
        console.log(`Обновлено количество для ${item.name}: ${newQuantity} из ${item.maxQuantity}`);
        
        // После изменения оборудования, обновляем недоступные даты
        setTimeout(fetchDisabledDatesForEquipment, 100);
    };

    // Обработчик отправки формы
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            if (!dateRange[0] || !dateRange[1]) {
                setError('Пожалуйста, выберите даты бронирования');
                setIsLoading(false);
                return;
            }

            if (selectedEquipment.length === 0) {
                setError('Пожалуйста, выберите хотя бы одну единицу оборудования');
                setIsLoading(false);
                return;
            }

            // Форматируем даты для отправки
            const bookingStartDate = new Date(dateRange[0]);
            const bookingEndDate = new Date(dateRange[1]);

            // Устанавливаем время 00:01:00 для даты начала (чтобы избежать ошибки с "датой в прошлом")
            bookingStartDate.setHours(0, 1, 0, 0);

            // Устанавливаем время 23:59:59 для даты конца
            bookingEndDate.setHours(23, 59, 59, 999);

            // Форматирование дат для Supabase (ISO формат)
            const formattedStartIso = bookingStartDate.toISOString();
            const formattedEndIso = bookingEndDate.toISOString();

            // Вычисляем реальное количество дней для бронирования
            const days = Math.floor((bookingEndDate.getTime() - bookingStartDate.getTime()) / (1000 * 60 * 60 * 24)) || 1;
            console.log(`Расчетное количество дней: ${days}`);

            // Подготовка данных для обновления бронирования
            const bookingData = {
                booking_id: bookingId,
                start_date: formattedStartIso,
                end_date: formattedEndIso,
                total_price: totalPrice,
                project_name: newProjectName.trim() || undefined,
                equipment_items: selectedEquipment.map(item => ({
                    equipment_id: item.id,
                    quantity: item.quantity,
                    daily_rate: item.dailyRate
                }))
            };

            console.log('Отправляемые данные бронирования:', JSON.stringify(bookingData));

            // Отправка данных в Supabase
            const { data, error } = await updateBooking(bookingData);

            if (error) {
                console.error('Ошибка при обновлении бронирования:', error);
                setError('Произошла ошибка при обновлении бронирования. Пожалуйста, попробуйте позже.');
                setIsLoading(false);
                return;
            }

            console.log('Бронирование успешно обновлено:', data);

            // Показываем сообщение об успехе
            toast.success('Бронирование успешно обновлено!');

            // Закрываем форму и обновляем список бронирований
            onSuccess();
        } catch (err) {
            console.error('Непредвиденная ошибка при обновлении бронирования:', err);
            setError('Непредвиденная ошибка при обновлении бронирования. Пожалуйста, попробуйте позже.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-4 max-h-[85vh] overflow-y-auto">
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Редактирование бронирования</h3>

                    {/* Секция выбора дат */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Даты аренды</label>
                        <DateRangeSelector
                            key={`date-selector-${key}`}
                            initialStartDate={calendarDates.initialStartDate}
                            initialEndDate={calendarDates.initialEndDate}
                            onDatesChange={handleDatesChange}
                            disabledDates={disabledDates}
                            forceOpen={forceOpenCalendar}
                        />
                    </div>

                    {/* Секция выбранного оборудования */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Выбранное оборудование</label>
                        <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-[20vh] overflow-y-auto">
                            {selectedEquipment.length > 0 ? (
                                <ul className="divide-y divide-gray-100">
                                    {selectedEquipment.map((item, index) => (
                                        <li key={item.id} className="py-2 flex justify-between items-center">
                                            <div className="flex items-center space-x-3">
                                                {item.image_url && (
                                                    <img
                                                        src={item.image_url}
                                                        alt={item.name}
                                                        className="w-10 h-10 object-cover rounded"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium">{item.name}</p>
                                                    <p className="text-sm text-gray-500">
                                                        {(item.dailyRate !== undefined && item.dailyRate !== null && item.dailyRate > 0) 
                                                            ? `${item.dailyRate.toLocaleString('ru-RU')} ₽/день`
                                                            : 'Цена не указана'}
                                                    </p>
                                                    {item.maxQuantity === 0 && (
                                                        <p className="text-xs text-orange-600 mt-1">
                                                            ⚠️ Добавлено с нулевым наличием
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                {/* Кнопка уменьшения количества */}
                                                <button 
                                                    type="button"
                                                    onClick={() => updateEquipmentQuantity(index, Math.max(1, item.quantity - 1))}
                                                    disabled={item.quantity <= 1}
                                                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                                    title="Уменьшить количество"
                                                >
                                                    <FiMinus className="w-4 h-4" />
                                                </button>
                                                <span className="mx-2">{item.quantity}</span>
                                                
                                                {/* Кнопка увеличения количества */}
                                                <div className="relative">
                                                    <button 
                                                        type="button"
                                                        onClick={() => updateEquipmentQuantity(index, item.quantity + 1)}
                                                        disabled={item.maxQuantity !== undefined && item.maxQuantity !== 0 && item.quantity >= item.maxQuantity}
                                                        className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                                        title={item.maxQuantity !== undefined && item.maxQuantity !== 0 && item.quantity >= item.maxQuantity
                                                            ? "Достигнуто максимальное количество" 
                                                            : "Увеличить количество"}
                                                    >
                                                        <FiPlus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveEquipment(item.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 text-center py-2">Нет выбранного оборудования</p>
                            )}
                        </div>
                    </div>

                    {/* Секция добавления оборудования */}
                    <div>
                        <p className="block text-sm font-medium text-gray-700 mb-1">Добавить оборудование:</p>
                        <EquipmentSelector
                            onSelect={handleEquipmentSelect}
                            dateRange={[dateRange[0], dateRange[1]]}
                            compact={true}
                            excludeIds={selectedEquipment.map(item => item.id)}
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Название проекта */}
                    <div>
                        <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-1">
                            Название проекта (необязательно):
                        </label>
                        <input
                            type="text"
                            id="projectName"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                            placeholder="Введите название проекта"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                        />
                    </div>

                    {/* Итоговая стоимость */}
                    <div className="flex justify-between items-center text-base font-bold mt-3">
                        <span>Итоговая стоимость:</span>
                        <span>{totalPrice.toLocaleString('ru-RU')} ₽</span>
                    </div>

                    {/* Сообщение об ошибке */}
                    {error && (
                        <div className="p-2 bg-red-100 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {/* Кнопки подтверждения/отмены */}
                    <div className="flex justify-end space-x-3 pt-3 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 rounded-lg text-white ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-600'
                                }`}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Сохранение...' : 'Сохранить изменения'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditBookingForm; 